import React, { useRef, useState } from 'react';
import type { NodeViewProps } from '@tiptap/react';
import { Box, Button, Chip, FormControlLabel, IconButton, MenuItem, Stack, Switch, Tooltip, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import TouchAppOutlined from '@mui/icons-material/TouchAppOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import Tune from '@mui/icons-material/Tune';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import ImageSearchOutlined from '@mui/icons-material/ImageSearchOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import { useNotify, useTranslate } from 'react-admin';
import { MediaPickerDialog, type PickedMedia } from '../../../../common/MediaPickerDialog';
import { useMediaObjectUrl } from '../../../../../hooks/useMediaObjectUrl';
import { BlockFrame } from './BlockFrame';
import { AttrTextField, stopEditorEvents } from './AttrFields';

/* ───────────────────────── shared ───────────────────────── */

const Remove: React.FC<{ onClick: () => void; disabled?: boolean; label: string }> = ({ onClick, disabled, label }) => (
    <Tooltip title={label}>
        <span><IconButton size="small" onClick={onClick} disabled={disabled} aria-label={label}><DeleteOutline fontSize="small" /></IconButton></span>
    </Tooltip>
);

const Explanation: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
    const translate = useTranslate();
    return <AttrTextField size="small" multiline minRows={2} fullWidth label={translate('blockEditor.views.explanation')} helperText={translate('blockEditor.views.explanationHint')} value={value ?? ''} onValueChange={onChange} />;
};

const clampPct = (v: number): number => Math.max(0, Math.min(100, Math.round(v * 10) / 10));

/**
 * Image canvas shared by hotspot and labeling: pick from the media library,
 * click to place a marker (percent coordinates), markers drawn as overlays.
 */
const ImageStage: React.FC<{ src: string; alt: string; onPick: (media: PickedMedia) => void; onPlace?: (x: number, y: number) => void; children?: React.ReactNode; emptyHint: string }> = ({ src, alt, onPick, onPlace, children, emptyHint }) => {
    const translate = useTranslate();
    const [pickerOpen, setPickerOpen] = useState(false);
    const objectUrl = useMediaObjectUrl(src || null);

    const place = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!onPlace) return;
        const rect = e.currentTarget.getBoundingClientRect();
        onPlace(clampPct(((e.clientX - rect.left) / rect.width) * 100), clampPct(((e.clientY - rect.top) / rect.height) * 100));
    };

    return (
        <Box {...stopEditorEvents}>
            {src ? (
                <div className="oa-block-stage" onClick={place} role={onPlace ? 'button' : undefined} data-testid="image-stage">
                    <img src={objectUrl ?? src} alt={alt} className="oa-block-stage-img" draggable={false} />
                    {children}
                </div>
            ) : (
                <div className="oa-block-stage-empty">
                    <ImageSearchOutlined color="disabled" />
                    <Typography variant="body2" color="text.secondary">{emptyHint}</Typography>
                </div>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" startIcon={<ImageSearchOutlined />} onClick={() => setPickerOpen(true)}>{translate(src ? 'blockEditor.views.stage.replace' : 'blockEditor.views.stage.choose')}</Button>
                {src && onPlace && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>{translate('blockEditor.views.stage.clickHint')}</Typography>}
            </Stack>
            <MediaPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} accept="image" onSelect={(m) => { onPick(m); setPickerOpen(false); }} />
        </Box>
    );
};

/* ───────────────────────── Hotspot ───────────────────────── */

interface HotspotArea { x: number; y: number; width: number; height: number; label: string; description: string }

export const HotspotBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { imageSrc, explanation } = node.attrs as { imageSrc: string; explanation: string };
    const areas: HotspotArea[] = Array.isArray(node.attrs.areas) ? (node.attrs.areas as HotspotArea[]) : [];
    const setAreas = (next: HotspotArea[]) => updateAttributes({ areas: next });
    const update = (i: number, patch: Partial<HotspotArea>) => setAreas(areas.map((a, k) => (k === i ? { ...a, ...patch } : a)));

    return (
        <BlockFrame type="hotspot-block" icon={TouchAppOutlined} title={translate('blockEditor.types.hotspot.label')} selected={selected} meta={translate('blockEditor.views.hotspot.count', { smart_count: areas.length })}>
            <Stack spacing={1.5}>
                <ImageStage
                    src={imageSrc}
                    alt={translate('blockEditor.types.hotspot.label')}
                    emptyHint={translate('blockEditor.views.hotspot.empty')}
                    onPick={(m) => updateAttributes({ imageSrc: `/api/media/${m.uuid}` })}
                    onPlace={(x, y) => setAreas([...areas, { x: clampPct(x - 10), y: clampPct(y - 10), width: 20, height: 20, label: '', description: '' }])}
                >
                    {areas.map((a, i) => (
                        <div key={i} className="oa-block-stage-area" style={{ left: `${a.x}%`, top: `${a.y}%`, width: `${a.width}%`, height: `${a.height}%` }}><span>{i + 1}</span></div>
                    ))}
                </ImageStage>
                {areas.map((a, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <span className="oa-block-index">{i + 1}</span>
                        <AttrTextField size="small" sx={{ minWidth: 140 }} label={translate('blockEditor.views.hotspot.label')} value={a.label} onValueChange={(v) => update(i, { label: v })} />
                        <AttrTextField size="small" sx={{ flex: 1, minWidth: 180 }} label={translate('blockEditor.views.hotspot.description')} value={a.description} onValueChange={(v) => update(i, { description: v })} />
                        {(['x', 'y', 'width', 'height'] as const).map((k) => (
                            <AttrTextField key={k} size="small" type="number" sx={{ width: 76 }} label={k} value={String(a[k])} onValueChange={(v) => update(i, { [k]: clampPct(Number(v) || 0) } as Partial<HotspotArea>)} />
                        ))}
                        <Remove onClick={() => setAreas(areas.filter((_, k) => k !== i))} label={translate('blockEditor.views.hotspot.remove')} />
                    </Stack>
                ))}
                {imageSrc && <Button size="small" startIcon={<Add />} onClick={() => setAreas([...areas, { x: 10, y: 10, width: 20, height: 20, label: '', description: '' }])} sx={{ alignSelf: 'flex-start' }}>{translate('blockEditor.views.hotspot.add')}</Button>}
                <Explanation value={explanation} onChange={(v) => updateAttributes({ explanation: v })} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Labeling ───────────────────────── */

interface LabelPoint { x: number; y: number; text: string }

export const LabelingBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { imageSrc, explanation } = node.attrs as { imageSrc: string; explanation: string };
    const labels: LabelPoint[] = Array.isArray(node.attrs.labels) ? (node.attrs.labels as LabelPoint[]) : [];
    const setLabels = (next: LabelPoint[]) => updateAttributes({ labels: next });
    const update = (i: number, patch: Partial<LabelPoint>) => setLabels(labels.map((l, k) => (k === i ? { ...l, ...patch } : l)));

    return (
        <BlockFrame type="labeling-block" icon={LabelOutlined} title={translate('blockEditor.types.labeling.label')} selected={selected} meta={translate('blockEditor.views.labeling.count', { smart_count: labels.length })}>
            <Stack spacing={1.5}>
                <ImageStage
                    src={imageSrc}
                    alt={translate('blockEditor.types.labeling.label')}
                    emptyHint={translate('blockEditor.views.labeling.empty')}
                    onPick={(m) => updateAttributes({ imageSrc: `/api/media/${m.uuid}` })}
                    onPlace={(x, y) => setLabels([...labels, { x, y, text: '' }])}
                >
                    {labels.map((l, i) => (
                        <div key={i} className="oa-block-stage-pin" style={{ left: `${l.x}%`, top: `${l.y}%` }}>{i + 1}</div>
                    ))}
                </ImageStage>
                {labels.map((l, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center">
                        <span className="oa-block-index">{i + 1}</span>
                        <AttrTextField size="small" fullWidth label={translate('blockEditor.views.labeling.text')} value={l.text} onValueChange={(v) => update(i, { text: v })} />
                        <AttrTextField size="small" type="number" sx={{ width: 76 }} label="x" value={String(l.x)} onValueChange={(v) => update(i, { x: clampPct(Number(v) || 0) })} />
                        <AttrTextField size="small" type="number" sx={{ width: 76 }} label="y" value={String(l.y)} onValueChange={(v) => update(i, { y: clampPct(Number(v) || 0) })} />
                        <Remove onClick={() => setLabels(labels.filter((_, k) => k !== i))} disabled={labels.length <= 1} label={translate('blockEditor.views.labeling.remove')} />
                    </Stack>
                ))}
                {imageSrc && <Button size="small" startIcon={<Add />} onClick={() => setLabels([...labels, { x: 50, y: 50, text: '' }])} sx={{ alignSelf: 'flex-start' }}>{translate('blockEditor.views.labeling.add')}</Button>}
                <Explanation value={explanation} onChange={(v) => updateAttributes({ explanation: v })} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Sorting ───────────────────────── */

interface SortCategory { label: string; items: string[] }

export const SortingBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { explanation } = node.attrs as { explanation: string };
    const categories: SortCategory[] = Array.isArray(node.attrs.categories) ? (node.attrs.categories as SortCategory[]) : [];
    const set = (next: SortCategory[]) => updateAttributes({ categories: next });

    return (
        <BlockFrame type="sorting-block" icon={CategoryOutlined} title={translate('blockEditor.types.sorting.label')} selected={selected} actions={<Button size="small" startIcon={<Add />} onClick={() => set([...categories, { label: translate('blockEditor.views.sorting.newCategory', { n: categories.length + 1 }), items: [''] }])}>{translate('blockEditor.views.sorting.addCategory')}</Button>}>
            <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(categories.length, 3)}, minmax(0, 1fr))`, gap: 1.5 }}>
                    {categories.map((cat, ci) => (
                        <Box key={ci} className="oa-block-subcard">
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <AttrTextField size="small" fullWidth label={translate('blockEditor.views.sorting.category')} value={cat.label} onValueChange={(v) => set(categories.map((c, k) => (k === ci ? { ...c, label: v } : c)))} />
                                <Remove onClick={() => set(categories.filter((_, k) => k !== ci))} disabled={categories.length <= 2} label={translate('blockEditor.views.sorting.removeCategory')} />
                            </Stack>
                            <Stack spacing={0.75}>
                                {cat.items.map((item, ii) => (
                                    <Stack key={ii} direction="row" spacing={0.5} alignItems="center">
                                        <AttrTextField size="small" fullWidth value={item} placeholder={translate('blockEditor.views.sorting.item', { n: ii + 1 })} onValueChange={(v) => set(categories.map((c, k) => (k === ci ? { ...c, items: c.items.map((it, j) => (j === ii ? v : it)) } : c)))} />
                                        <Remove onClick={() => set(categories.map((c, k) => (k === ci ? { ...c, items: c.items.filter((_, j) => j !== ii) } : c)))} disabled={cat.items.length <= 1} label={translate('blockEditor.views.sorting.removeItem')} />
                                    </Stack>
                                ))}
                            </Stack>
                            <Button size="small" startIcon={<Add />} onClick={() => set(categories.map((c, k) => (k === ci ? { ...c, items: [...c.items, ''] } : c)))} sx={{ mt: 0.5 }}>{translate('blockEditor.views.sorting.addItem')}</Button>
                        </Box>
                    ))}
                </Box>
                <Explanation value={explanation} onChange={(v) => updateAttributes({ explanation: v })} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Slider scale ───────────────────────── */

export const SliderScaleBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { question, minLabel, maxLabel, minValue, maxValue, step } = node.attrs as { question: string; minLabel: string; maxLabel: string; minValue: number; maxValue: number; step: number };

    return (
        <BlockFrame type="slider-scale-block" icon={Tune} title={translate('blockEditor.types.slider_scale.label')} selected={selected} meta={`${minValue} – ${maxValue}`}>
            <Stack spacing={1.5}>
                <AttrTextField size="small" fullWidth label={translate('blockEditor.views.sliderScale.question')} value={question} onValueChange={(v) => updateAttributes({ question: v })} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <AttrTextField size="small" fullWidth label={translate('blockEditor.views.sliderScale.minLabel')} value={minLabel ?? ''} onValueChange={(v) => updateAttributes({ minLabel: v })} />
                    <AttrTextField size="small" fullWidth label={translate('blockEditor.views.sliderScale.maxLabel')} value={maxLabel ?? ''} onValueChange={(v) => updateAttributes({ maxLabel: v })} />
                </Stack>
                <Stack direction="row" spacing={1.5}>
                    <AttrTextField size="small" type="number" label={translate('blockEditor.views.sliderScale.min')} value={String(minValue)} onValueChange={(v) => updateAttributes({ minValue: Number(v) || 0 })} />
                    <AttrTextField size="small" type="number" label={translate('blockEditor.views.sliderScale.max')} value={String(maxValue)} onValueChange={(v) => updateAttributes({ maxValue: Number(v) || 0 })} />
                    <AttrTextField size="small" type="number" label={translate('blockEditor.views.sliderScale.step')} value={String(step)} onValueChange={(v) => updateAttributes({ step: Math.max(1, Number(v) || 1) })} />
                </Stack>
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Discussion ───────────────────────── */

export const DiscussionBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { prompt, allowReplies, moderationRequired } = node.attrs as { prompt: string; allowReplies: boolean; moderationRequired: boolean };

    return (
        <BlockFrame type="discussion-block" icon={ForumOutlined} title={translate('blockEditor.types.discussion.label')} selected={selected}>
            <Stack spacing={1.5}>
                <AttrTextField size="small" fullWidth multiline minRows={2} label={translate('blockEditor.views.discussion.prompt')} value={prompt} onValueChange={(v) => updateAttributes({ prompt: v })} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControlLabel control={<Switch size="small" checked={allowReplies !== false} onChange={(e) => updateAttributes({ allowReplies: e.target.checked })} />} label={<Typography variant="body2">{translate('blockEditor.views.discussion.allowReplies')}</Typography>} />
                    <FormControlLabel control={<Switch size="small" checked={!!moderationRequired} onChange={(e) => updateAttributes({ moderationRequired: e.target.checked })} />} label={<Typography variant="body2">{translate('blockEditor.views.discussion.moderation')}</Typography>} />
                </Stack>
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Branching ───────────────────────── */

interface BranchOption { label: string; nextNodeId?: string }
interface BranchNode { id: string; question: string; options: BranchOption[] }
const END = '__end__';

export const BranchingBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { startNodeId } = node.attrs as { startNodeId: string };
    const nodes: BranchNode[] = Array.isArray(node.attrs.nodes) ? (node.attrs.nodes as BranchNode[]) : [];
    const set = (next: BranchNode[], extra: Record<string, unknown> = {}) => updateAttributes({ nodes: next, ...extra });
    const nodeId = () => `n${nodes.length + 1}-${Math.random().toString(36).slice(2, 6)}`;

    return (
        <BlockFrame type="branching-block" icon={AccountTreeOutlined} title={translate('blockEditor.types.branching.label')} selected={selected} meta={translate('blockEditor.views.branching.count', { smart_count: nodes.length })} actions={<Button size="small" startIcon={<Add />} onClick={() => set([...nodes, { id: nodeId(), question: '', options: [{ label: '' }, { label: '' }] }])}>{translate('blockEditor.views.branching.addNode')}</Button>}>
            <Stack spacing={1.5}>
                <AttrTextField select size="small" sx={{ maxWidth: 360 }} label={translate('blockEditor.views.branching.start')} value={nodes.some((n) => n.id === startNodeId) ? startNodeId : nodes[0]?.id ?? ''} onValueChange={(v) => updateAttributes({ startNodeId: v })}>
                    {nodes.map((n) => <MenuItem key={n.id} value={n.id}>{n.id}{n.question ? ` — ${n.question.slice(0, 40)}` : ''}</MenuItem>)}
                </AttrTextField>
                {nodes.map((bn, ni) => (
                    <Box key={bn.id} className="oa-block-subcard">
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Chip size="small" label={bn.id} />
                            {bn.id === startNodeId && <Chip size="small" color="success" variant="outlined" label={translate('blockEditor.views.branching.startChip')} />}
                            <Box sx={{ flex: 1 }} />
                            <Remove onClick={() => { const next = nodes.filter((_, k) => k !== ni); set(next, startNodeId === bn.id ? { startNodeId: next[0]?.id ?? '' } : {}); }} disabled={nodes.length <= 1} label={translate('blockEditor.views.branching.removeNode')} />
                        </Stack>
                        <AttrTextField size="small" fullWidth label={translate('blockEditor.views.branching.question')} value={bn.question} onValueChange={(v) => set(nodes.map((n, k) => (k === ni ? { ...n, question: v } : n)))} sx={{ mb: 1 }} />
                        <Stack spacing={0.75}>
                            {bn.options.map((o, oi) => (
                                <Stack key={oi} direction="row" spacing={1} alignItems="center">
                                    <AttrTextField size="small" fullWidth label={translate('blockEditor.views.branching.option')} value={o.label} onValueChange={(v) => set(nodes.map((n, k) => (k === ni ? { ...n, options: n.options.map((x, j) => (j === oi ? { ...x, label: v } : x)) } : n)))} />
                                    <AttrTextField select size="small" sx={{ minWidth: 170 }} label={translate('blockEditor.views.branching.next')} value={o.nextNodeId || END} onValueChange={(v) => set(nodes.map((n, k) => (k === ni ? { ...n, options: n.options.map((x, j) => (j === oi ? (v === END ? { label: x.label } : { ...x, nextNodeId: v }) : x)) } : n)))}>
                                        <MenuItem value={END}>{translate('blockEditor.views.branching.end')}</MenuItem>
                                        {nodes.filter((n) => n.id !== bn.id).map((n) => <MenuItem key={n.id} value={n.id}>{n.id}</MenuItem>)}
                                    </AttrTextField>
                                    <Remove onClick={() => set(nodes.map((n, k) => (k === ni ? { ...n, options: n.options.filter((_, j) => j !== oi) } : n)))} disabled={bn.options.length <= 1} label={translate('blockEditor.views.branching.removeOption')} />
                                </Stack>
                            ))}
                        </Stack>
                        <Button size="small" startIcon={<Add />} onClick={() => set(nodes.map((n, k) => (k === ni ? { ...n, options: [...n.options, { label: '' }] } : n)))} sx={{ mt: 0.5 }}>{translate('blockEditor.views.branching.addOption')}</Button>
                    </Box>
                ))}
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── Simulation ───────────────────────── */

interface SimChoice { label: string; feedback?: string; nextStepId?: string; isCorrect?: boolean }
interface SimStep { id: string; text: string; choices: SimChoice[] }

export const SimulationBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { scenario } = node.attrs as { scenario: string };
    const steps: SimStep[] = Array.isArray(node.attrs.steps) ? (node.attrs.steps as SimStep[]) : [];
    const set = (next: SimStep[]) => updateAttributes({ steps: next });
    const patchChoice = (si: number, ci: number, patch: Partial<SimChoice>) => set(steps.map((s, k) => (k === si ? { ...s, choices: s.choices.map((c, j) => (j === ci ? { ...c, ...patch } : c)) } : s)));

    return (
        <BlockFrame type="simulation-block" icon={RouteOutlined} title={translate('blockEditor.types.simulation.label')} selected={selected} meta={translate('blockEditor.views.simulation.count', { smart_count: steps.length })} actions={<Button size="small" startIcon={<Add />} onClick={() => set([...steps, { id: `s${steps.length + 1}`, text: '', choices: [{ label: '', feedback: '', isCorrect: true }, { label: '', feedback: '', isCorrect: false }] }])}>{translate('blockEditor.views.simulation.addStep')}</Button>}>
            <Stack spacing={1.5}>
                <AttrTextField size="small" fullWidth multiline minRows={2} label={translate('blockEditor.views.simulation.scenario')} value={scenario} onValueChange={(v) => updateAttributes({ scenario: v })} />
                {steps.map((st, si) => (
                    <Box key={st.id} className="oa-block-subcard">
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <Chip size="small" label={st.id} />
                            {si === 0 && <Chip size="small" color="success" variant="outlined" label={translate('blockEditor.views.branching.startChip')} />}
                            <Box sx={{ flex: 1 }} />
                            <Remove onClick={() => set(steps.filter((_, k) => k !== si))} disabled={steps.length <= 1} label={translate('blockEditor.views.simulation.removeStep')} />
                        </Stack>
                        <AttrTextField size="small" fullWidth multiline minRows={2} label={translate('blockEditor.views.simulation.stepText')} value={st.text} onValueChange={(v) => set(steps.map((s, k) => (k === si ? { ...s, text: v } : s)))} sx={{ mb: 1 }} />
                        <Stack spacing={1}>
                            {st.choices.map((c, ci) => (
                                <Box key={ci} className="oa-block-subcard" sx={{ background: '#fff' }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <AttrTextField size="small" sx={{ flex: 1, minWidth: 180 }} label={translate('blockEditor.views.simulation.choice')} value={c.label} onValueChange={(v) => patchChoice(si, ci, { label: v })} />
                                        <FormControlLabel control={<Switch size="small" checked={!!c.isCorrect} onChange={(e) => patchChoice(si, ci, { isCorrect: e.target.checked })} />} label={<Typography variant="body2">{translate('blockEditor.views.simulation.correct')}</Typography>} />
                                        <AttrTextField select size="small" sx={{ minWidth: 150 }} label={translate('blockEditor.views.branching.next')} value={c.nextStepId || END} onValueChange={(v) => patchChoice(si, ci, { nextStepId: v === END ? undefined : v })}>
                                            <MenuItem value={END}>{translate('blockEditor.views.branching.end')}</MenuItem>
                                            {steps.filter((s) => s.id !== st.id).map((s) => <MenuItem key={s.id} value={s.id}>{s.id}</MenuItem>)}
                                        </AttrTextField>
                                        <Remove onClick={() => set(steps.map((s, k) => (k === si ? { ...s, choices: s.choices.filter((_, j) => j !== ci) } : s)))} disabled={st.choices.length <= 1} label={translate('blockEditor.views.simulation.removeChoice')} />
                                    </Stack>
                                    <AttrTextField size="small" fullWidth label={translate('blockEditor.views.simulation.feedback')} value={c.feedback ?? ''} onValueChange={(v) => patchChoice(si, ci, { feedback: v })} sx={{ mt: 1 }} />
                                </Box>
                            ))}
                        </Stack>
                        <Button size="small" startIcon={<Add />} onClick={() => set(steps.map((s, k) => (k === si ? { ...s, choices: [...s.choices, { label: '', feedback: '', isCorrect: false }] } : s)))} sx={{ mt: 0.5 }}>{translate('blockEditor.views.simulation.addChoice')}</Button>
                    </Box>
                ))}
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── SCORM ───────────────────────── */

export const ScormEmbedBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const { packageUrl, title, version, mediaFileUuid } = node.attrs as { packageUrl: string; title: string; version: string; mediaFileUuid: string };
    const [pickerOpen, setPickerOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileInput = useRef<HTMLInputElement>(null);

    const attach = async (uuid: string, name: string) => {
        setBusy(true);
        try {
            // Lazy: axios is ESM-only and must not be pulled into the editor bundle's test graph.
            const { default: api } = await import('../../../../../services/api');
            const { data } = await api.post(`/scorm/extract/${uuid}`);
            const detected = data?.version ?? data?.scormVersion ?? null;
            updateAttributes({ packageUrl: `/api/media/${uuid}`, mediaFileUuid: uuid, launchUrl: `/api/scorm/player/${uuid}`, title: title || name.replace(/\.zip$/i, ''), ...(detected === '2004' || String(detected).startsWith('2004') ? { version: '2004' } : {}) });
            notify(translate('blockEditor.views.scorm.ready'), { type: 'success' });
        } catch {
            notify(translate('blockEditor.views.scorm.extractError'), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const upload = async (file: File | undefined) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.zip')) { notify(translate('blockEditor.views.scorm.zipOnly'), { type: 'error' }); return; }
        setBusy(true);
        try {
            const { default: api } = await import('../../../../../services/api');
            const form = new FormData();
            form.append('file', file);
            const { data } = await api.post('/media/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
            const uuid = data?.file?.uuid ?? data?.uuid;
            if (!uuid) throw new Error('no uuid');
            await attach(uuid, file.name);
        } catch {
            notify(translate('blockEditor.views.scorm.uploadError'), { type: 'error' });
            setBusy(false);
        } finally {
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    return (
        <BlockFrame type="scorm-embed-block" icon={Inventory2Outlined} title={translate('blockEditor.types.scorm_embed.label')} selected={selected} meta={packageUrl ? `SCORM ${version || '1.2'}` : undefined}>
            <Stack spacing={1.5} {...stopEditorEvents}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <AttrTextField size="small" fullWidth label={translate('blockEditor.views.scorm.title')} value={title ?? ''} onValueChange={(v) => updateAttributes({ title: v })} />
                    <AttrTextField select size="small" sx={{ minWidth: 160 }} label={translate('blockEditor.views.scorm.version')} value={version || '1.2'} onValueChange={(v) => updateAttributes({ version: v })}>
                        <MenuItem value="1.2">SCORM 1.2</MenuItem>
                        <MenuItem value="2004">SCORM 2004</MenuItem>
                    </AttrTextField>
                </Stack>
                <input ref={fileInput} type="file" accept=".zip" hidden data-testid="scorm-file" onChange={(e) => void upload(e.target.files?.[0])} />
                {packageUrl ? (
                    <Typography variant="body2" data-testid="scorm-ready">{translate('blockEditor.views.scorm.attached', { title: title || mediaFileUuid })}</Typography>
                ) : (
                    <Typography variant="body2" color="text.secondary">{translate('blockEditor.views.scorm.empty')}</Typography>
                )}
                <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => fileInput.current?.click()}>{translate(packageUrl ? 'blockEditor.views.scorm.replace' : 'blockEditor.views.scorm.upload')}</Button>
                    <Button size="small" variant="outlined" disabled={busy} onClick={() => setPickerOpen(true)}>{translate('blockEditor.views.scorm.library')}</Button>
                    {busy && <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>{translate('blockEditor.views.scorm.processing')}</Typography>}
                </Stack>
                <MediaPickerDialog open={pickerOpen} onClose={() => setPickerOpen(false)} accept="any" onSelect={(m) => { setPickerOpen(false); void attach(m.uuid, m.originalFilename); }} />
            </Stack>
        </BlockFrame>
    );
};

/* ───────────────────────── H5P ───────────────────────── */

export const H5pBlockView: React.FC<NodeViewProps> = ({ node, selected, updateAttributes }) => {
    const translate = useTranslate();
    const { embedUrl, title, contentId } = node.attrs as { embedUrl: string; title: string; contentId: string | number | null };
    const getToken = () => localStorage.getItem('token') || '';

    const openEditor = () => {
        const idParam = contentId ? String(contentId) : 'new';
        const w = window.open(`/h5p/${idParam}/edit?token=${encodeURIComponent(getToken())}`, 'h5p-editor', 'width=1200,height=800,menubar=no,toolbar=no');
        const handler = (e: MessageEvent) => {
            if (e.data?.type === 'h5p-saved' && e.data.contentId) {
                updateAttributes({ contentId: String(e.data.contentId), embedUrl: `/h5p/${e.data.contentId}/play`, title: title || e.data.title || '' });
                window.removeEventListener('message', handler);
                w?.close();
            }
        };
        window.addEventListener('message', handler);
    };

    return (
        <BlockFrame type="h5p-block" icon={ExtensionOutlined} title={translate('blockEditor.types.h5p.label')} selected={selected} meta={contentId ? `#${contentId}` : undefined}>
            <Stack spacing={1.5} {...stopEditorEvents}>
                <AttrTextField size="small" fullWidth label={translate('blockEditor.views.h5p.title')} value={title ?? ''} onValueChange={(v) => updateAttributes({ title: v })} />
                <AttrTextField size="small" fullWidth label={translate('blockEditor.views.h5p.embedUrl')} helperText={translate('blockEditor.views.h5p.embedHint')} value={embedUrl ?? ''} onValueChange={(v) => updateAttributes({ embedUrl: v })} />
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" variant="outlined" startIcon={<OpenInNewOutlined />} onClick={openEditor}>{translate(contentId ? 'blockEditor.views.h5p.edit' : 'blockEditor.views.h5p.create')}</Button>
                    <Typography variant="caption" color="text.secondary">{contentId ? translate('blockEditor.views.h5p.attached', { id: String(contentId) }) : translate('blockEditor.views.h5p.empty')}</Typography>
                </Stack>
            </Stack>
        </BlockFrame>
    );
};
